import SitePageLayout from '../components/SitePageLayout';

export default function PrivacyPolicyScreen() {
  return (
    <SitePageLayout title="Privacy Policy" effectiveDate="March 24, 2026">
      <p>
        Wreckoning Ball (the "App") is a product of Xerratus, LLC ("Company," "we," "us," or "our").
        The App provides daily sign-based readings, personalized dooms, Ball responses, reactions,
        bookmarks, sharing tools, theme settings, and account features. This Privacy Policy explains
        what we collect, how we use it, and what choices you have.
      </p>
      <p>By using Wreckoning Ball, you agree to this Privacy Policy.</p>

      <section className="legal-section">
        <h2>1. Information We Collect</h2>
        <p>We may collect the following categories of information:</p>
        <ul>
          <li>
            Account and profile data: information such as your user ID, username, display name,
            email address, linked sign-in method, birthdate, zodiac sign, language preference,
            theme preference, and startup/settings preferences.
          </li>
          <li>
            Optional personalization data: details you choose to provide for more personalized
            readings, such as city, relationship status, work situation, living situation,
            birth details, stressors, or similar profile context.
          </li>
          <li>
            Doom activity data: records of daily dooms shown to you, personalized dooms generated
            for you, Ball interactions, reactions, bookmarks, shares, and related counts or state.
          </li>
          <li>
            Notification data: push notification permission status and device push tokens used to
            send notifications.
          </li>
          <li>
            Purchase and entitlement data: purchase status, subscription status, restore events,
            and related store product identifiers.
          </li>
          <li>
            Analytics and diagnostics: app usage events, screen views, device/app metadata, and
            similar technical information used to understand performance and product usage.
          </li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>2. How We Use Information</h2>
        <p>We use information to:</p>
        <ul>
          <li>Provide the App, including daily readings, personalized dooms, and Ball responses.</li>
          <li>Set up and maintain your account and sign-in methods.</li>
          <li>Personalize readings using your birthdate, sign, display name, and any optional profile context you provide.</li>
          <li>Store your reactions, bookmarks, shares, preferences, and settings.</li>
          <li>Send push notifications if you enable them.</li>
          <li>Process purchases, restore entitlements, and manage subscriptions.</li>
          <li>Measure usage, improve quality, detect issues, and protect the service.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>3. AI Processing</h2>
        <p>
          Wreckoning Ball uses AI for certain features, including Personalized Daily Dooms and other
          AI-dependent reading workflows.
        </p>
        <p>When AI processing is enabled, we may send the following to our AI service provider:</p>
        <ul>
          <li>Your birthdate and zodiac-related information.</li>
          <li>Your display name.</li>
          <li>Optional personalization details you choose to add in Profile.</li>
          <li>Minimal request metadata needed to process the request and operate the feature.</li>
        </ul>
        <p>
          AI processing is optional in the App settings. If you do not allow AI processing,
          AI-dependent features may not function.
        </p>
      </section>

      <section className="legal-section">
        <h2>4. Analytics, Advertising ID, and Diagnostics</h2>
        <ul>
          <li>We do not show third-party ads.</li>
          <li>We use Firebase Analytics and related diagnostics to understand app usage and stability.</li>
          <li>
            On supported Android devices, analytics tooling may access the device advertising identifier
            or similar identifiers for analytics and attribution purposes.
          </li>
          <li>We do not use your data to serve third-party ads inside the App.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>5. Notifications</h2>
        <p>
          If you allow notifications, we may store your push token and send notifications related to
          daily dooms, personalized dooms, friend requests, birthday messages, or similar app events.
          You can disable notifications in your device settings.
        </p>
      </section>

      <section className="legal-section">
        <h2>6. Sharing Features</h2>
        <ul>
          <li>You can choose to share doom images or content to third-party apps and services.</li>
          <li>Your shared images may include your username or display information, depending on the share format.</li>
          <li>Once you share content outside the App, the receiving service's privacy rules apply.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>7. Purchases</h2>
        <p>Wreckoning Ball offers in-app purchases and subscription features.</p>
        <ul>
          <li>Billing is handled by Apple App Store and Google Play, not by us directly.</li>
          <li>We receive purchase status, product identifiers, and entitlement information needed to unlock paid features.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>8. How We Share Information</h2>
        <p>
          We do not sell personal data. We may use service providers that process data on our
          behalf to host and operate the App, including:
        </p>
        <ul>
          <li>Firebase / Google for authentication, database, cloud messaging, analytics, and app infrastructure.</li>
          <li>OpenAI for AI-powered features that you choose to enable.</li>
          <li>Apple and Google for sign-in and in-app purchase processing.</li>
          <li>Third-party apps or services you explicitly choose when using sharing features.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>9. Data Retention</h2>
        <p>
          We retain data only as needed to provide the service, maintain security, resolve
          disputes, and comply with legal obligations.
        </p>
        <p>
          If you delete your account through the App, we attempt to remove your account record,
          profile data, settings, and related app data from our systems, subject to technical,
          legal, or backup-related limits.
        </p>
      </section>

      <section className="legal-section">
        <h2>10. Security</h2>
        <p>
          We use reasonable technical and organizational safeguards, but no system is completely
          secure.
        </p>
      </section>

      <section className="legal-section">
        <h2>11. Your Choices</h2>
        <ul>
          <li>You can choose whether to enable AI processing in Settings.</li>
          <li>You can edit profile information such as display name, username, birthdate, and language preferences.</li>
          <li>You can enable or disable push notifications in your device settings.</li>
          <li>You can delete your account from the App, subject to reauthentication requirements imposed by your sign-in provider.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>12. Children</h2>
        <p>
          Wreckoning Ball is not directed to children under 13 (or the minimum age required by local
          law).
        </p>
      </section>

      <section className="legal-section">
        <h2>13. Changes to This Policy</h2>
        <p>
          We may update this policy from time to time and will update the date above when we do.
        </p>
      </section>

      <section className="legal-section">
        <h2>14. Contact</h2>
        <p>For privacy questions: privacy@xerratus.com</p>
      </section>
    </SitePageLayout>
  );
}
